import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from '../../api';
import './timeline.css';
import {
  buildProcessMetadata,
  computeFadeOpacity,
  computeGlobalFilterCss,
  computeKenScale,
  computeOverlayBgStyle,
  computeOverlayGroupStyle,
  computeOverlayMediaStyle,
  computeVignetteCss,
  defaultGlobalFx,
  defaultOverlay,
  defaultOverlayBg,
  defaultTransition,
  GLOBAL_FX_CONTROLS,
  OVERLAY_BG_TYPES,
  OVERLAY_FIT_MODES,
  hasAnyGlobalFx,
  isTimeInOverlay,
  overlaysAtPlayhead,
  resolveOverlaySrc,
  TRANSITIONS,
} from './timelineFx';

export default function Timeline({ initialVideoUrl = "" }) {
  // Captions are handled by TTS during main video generation — not in this editor.
  const location = useLocation();
  // Support receiving video from CompletedVids "Send to Timeline" button
  const passedVideo = location.state?.timelineVideo || null;

  // ─── State ────────────────────────────────────────────────────
  const [videoUrl, setVideoUrl] = useState(passedVideo?.url || initialVideoUrl);
  const [videoFileName, setVideoFileName] = useState(passedVideo?.filename || "");
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [pixelsPerSecond, setPixelsPerSecond] = useState(120);
  const [overlays, setOverlays] = useState([]);
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);
  const [followPlayhead, setFollowPlayhead] = useState(false);

  // Global FX — single registry-driven object (see GLOBAL_FX_CONTROLS)
  const [fx, setFx] = useState(defaultGlobalFx());
  const setFxVal = useCallback((key, value) => setFx((p) => ({ ...p, [key]: value })), []);
  // Noise preview particle params (preview-only tuning)
  const [dotSize, setDotSize] = useState(3);
  const [blinkMs, setBlinkMs] = useState(140);

  // Panel active tab
  const [panelTab, setPanelTab] = useState("inspector"); // 'inspector' | 'fx' | 'list'

  // Thumbnails
  const [thumbs, setThumbs] = useState([]);
  const [thumbEverySec, setThumbEverySec] = useState(1);
  const [exactFpsMode, setExactFpsMode] = useState(false);

  // Quick overlay URL
  const [quickUrl, setQuickUrl] = useState("");

  // Drag
  const [drag, setDrag] = useState({ mode: null, overlayId: null, anchorTime: 0, startAt: 0, endAt: 0 });
  const [ghostCreate, setGhostCreate] = useState(null);

  // ─── Refs ──────────────────────────────────────────────────────
  const videoRef = useRef(null);
  const captureVideoRef = useRef(null);
  const noiseCanvasRef = useRef(null);
  const timelineScrollRef = useRef(null);
  const particlesRef = useRef([]);
  const spawnCarryRef = useRef(0);
  const lastTimeRef = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const rafRef = useRef(null);
  const objectUrlsRef = useRef(new Set());
  const prevBaseObjectUrlRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Drag bookkeeping: suppress the click that fires after a real drag,
  // and track whether the pointer actually moved during move/resize.
  const suppressClickRef = useRef(false);
  const movedRef = useRef(false);

  // ─── Auto-scroll RAF for edge dragging ───────────────────────
  const autoScrollRaf = useRef(null);

  // ─── Constants ────────────────────────────────────────────────
  const TIMELINE_PAD_LEFT = 10;
  const MIN_OVERLAY_LEN = 0.05;
  const DEFAULT_NEW_LEN = 2;

  // ─── Utils ────────────────────────────────────────────────────
  const clamp = (n, min, max) => Math.min(Math.max(Number.isFinite(+n) ? +n : 0, min), max);

  const detectType = (url) => {
    const u = (url || "").toLowerCase();
    if (u.match(/\.(png|jpg|jpeg|webp|gif|bmp|svg)(\?.*)?$/)) return "image";
    if (u.match(/\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/)) return "video";
    return "image";
  };

  const formatClock = (sec) => {
    if (!isFinite(sec)) return "0:00";
    const s = Math.max(0, sec);
    const m = Math.floor(s / 60);
    const r = s - m * 60;
    return `${m}:${Math.floor(r).toString().padStart(2, "0")}`;
  };

  const formatClockFull = (sec) => {
    if (!isFinite(sec)) return "0:00.00";
    const s = Math.max(0, sec);
    const m = Math.floor(s / 60);
    const r = s - m * 60;
    const whole = Math.floor(r);
    const ms = Math.floor((r - whole) * 100);
    return `${m}:${whole.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
  };

  const toAbsoluteUrl = (maybeUrl) => {
    try { return new URL(maybeUrl).toString(); }
    catch { return new URL(maybeUrl, (api?.defaults?.baseURL) || window.location.origin).toString(); }
  };

  const getMouseTime = useCallback((clientX) => {
    const scroller = timelineScrollRef.current;
    if (!scroller) return 0;
    const rect = scroller.getBoundingClientRect();
    const x = clientX - rect.left + scroller.scrollLeft - TIMELINE_PAD_LEFT;
    return Math.max(0, Math.min(duration || 0, x / pixelsPerSecond));
  }, [pixelsPerSecond, duration]);

  // Touch equivalent of getMouseTime
  const getTouchTime = useCallback((touch) => {
    return getMouseTime(touch.clientX);
  }, [getMouseTime]);

  const handleSeek = useCallback((t) => {
    const clamped = Math.min(Math.max(0, t), duration || 0);
    // Update preview immediately (don't wait for video timeupdate)
    setCurrentTime(clamped);
    const v = videoRef.current;
    if (!v) return;
    try {
      v.currentTime = clamped;
    } catch (_) {}
  }, [duration]);

  // ─── Upload ───────────────────────────────────────────────────
  const uploadFileToBackend = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.url;
  };

  // ─── Process ──────────────────────────────────────────────────
  const sendVideoMetadataToBackend = async () => {
    if (isSubmittingRef.current) return;
    if (!videoFileName || typeof videoFileName !== "string") {
      alert("❌ Missing video file name."); return;
    }
    const metadata = buildProcessMetadata({
      videoFileName,
      duration,
      overlays,
      fx,
    });

    if (!metadata.overlays.length && !hasAnyGlobalFx(fx)) {
      alert("❌ Nothing to process. Add overlays or enable an effect."); return;
    }

    isSubmittingRef.current = true;
    try {
      const response = await api.post("/process_video", metadata, {
        headers: { "Content-Type": "application/json", "Accept": "application/json" }
      });
      const data = response?.data || {};
      const url = data.processedVideoUrl || data.url || data.file || null;
      if (!url) { alert("❌ Backend did not return a processed video URL."); return; }
      alert(`✅ Video processed!\n${toAbsoluteUrl(url)}`);
    } catch (err) {
      if (err?.response) {
        alert(`❌ Backend error (${err.response.status}): ${err.response.data?.error || "Processing failed."}`);
      } else {
        alert("❌ Network error or unknown issue.");
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // ─── Drag Callbacks ───────────────────────────────────────────
  const onPlayheadMouseDown = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setSelectedOverlayId(null);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setDrag({ mode: 'playhead', overlayId: null, anchorTime: getMouseTime(clientX), startAt: 0, endAt: 0 });
  }, [getMouseTime]);

  const beginMove = useCallback((id, e) => {
    e.preventDefault(); e.stopPropagation();
    const ov = overlays.find(o => o.id === id); if (!ov) return;
    setSelectedOverlayId(id);
    movedRef.current = false;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setDrag({ mode: 'move', overlayId: id, anchorTime: getMouseTime(clientX), startAt: ov.start, endAt: ov.end });
  }, [getMouseTime, overlays]);

  const beginResize = useCallback((side, id, e) => {
    e.preventDefault(); e.stopPropagation();
    const ov = overlays.find(o => o.id === id); if (!ov) return;
    setSelectedOverlayId(id);
    movedRef.current = false;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setDrag({
      mode: side === 'l' ? 'resize-l' : 'resize-r',
      overlayId: id, anchorTime: getMouseTime(clientX), startAt: ov.start, endAt: ov.end
    });
  }, [getMouseTime, overlays]);

  // Drag on empty track = create overlay (was dead code before: nothing
  // ever entered 'create' mode). Playhead grab / overlays stopPropagation.
  const beginCreate = useCallback((e) => {
    const target = e.target;
    if (target?.dataset?.role) return; // overlay / handle / playhead
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const t = getMouseTime(clientX);
    movedRef.current = false;
    setDrag({ mode: 'create', overlayId: null, anchorTime: t, startAt: 0, endAt: 0 });
  }, [getMouseTime]);

  // ─── Edge auto-scroll helper ─────────────────────────────────
  const runAutoScroll = useCallback((clientX) => {
    const scroller = timelineScrollRef.current;
    if (!scroller) return;
    const rect = scroller.getBoundingClientRect();
    const HOT = 60;          // px from edge that triggers scroll
    const MAX_SPEED = 18;    // max px per frame
    let speed = 0;

    const distFromLeft  = clientX - rect.left;
    const distFromRight = rect.right - clientX;

    if (distFromRight < HOT) {
      speed = MAX_SPEED * (1 - distFromRight / HOT);
    } else if (distFromLeft < HOT) {
      speed = -MAX_SPEED * (1 - distFromLeft / HOT);
    }

    if (speed !== 0) {
      scroller.scrollLeft = Math.max(
        0,
        Math.min(scroller.scrollWidth - scroller.clientWidth, scroller.scrollLeft + speed)
      );
    }
    return speed;
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRaf.current) {
      cancelAnimationFrame(autoScrollRaf.current);
      autoScrollRaf.current = null;
    }
  }, []);

  // ─── Create overlay at playhead ──────────────────────────────
  const createOverlayAtPlayhead = useCallback(() => {
    const start = Math.min(currentTime, Math.max(0, (duration || 0) - DEFAULT_NEW_LEN));
    const end = Math.min(duration || (start + DEFAULT_NEW_LEN), start + DEFAULT_NEW_LEN);
    const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setOverlays(prev => [...prev, { id, ...defaultOverlay(start, end) }]);
    setSelectedOverlayId(id);
    setPanelTab("inspector");
  }, [currentTime, duration]);


  // ─── Global pointer/touch move + up handlers ─────────────────
  useEffect(() => {
    if (!drag.mode) return;

    const getClientXY = (e) => {
      if (e.touches && e.touches.length > 0) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      return { x: e.clientX, y: e.clientY };
    };

    const onMove = (e) => {
      const { x: clientX } = getClientXY(e);

      // ── Auto-scroll at edges during any active drag ──
      const loopAutoScroll = () => {
        const speed = runAutoScroll(clientX);
        if (speed !== 0 && drag.mode) {
          autoScrollRaf.current = requestAnimationFrame(loopAutoScroll);
        } else {
          stopAutoScroll();
        }
      };
      stopAutoScroll();
      autoScrollRaf.current = requestAnimationFrame(loopAutoScroll);

      const t = getMouseTime(clientX);
      if (Math.abs(t - drag.anchorTime) > 0.01) movedRef.current = true;

      if (drag.mode === 'playhead') {
        handleSeek(t);
        return;
      }

      if (drag.mode === 'create') {
        const s = Math.min(drag.anchorTime, t);
        const en = Math.max(drag.anchorTime, t);
        setGhostCreate({ start: Math.max(0, s), end: Math.min(duration || 0, en) });
        return;
      }

      // move / resize-l / resize-r
      setOverlays(prev => prev.map(o => {
        if (o.id !== drag.overlayId) return o;
        if (drag.mode === 'move') {
          const length = drag.endAt - drag.startAt;
          let ns = drag.startAt + (t - drag.anchorTime);
          ns = Math.max(0, Math.min((duration || 0) - length, ns));
          return { ...o, start: ns, end: ns + length };
        }
        if (drag.mode === 'resize-l') {
          return { ...o, start: Math.max(0, Math.min(t, drag.endAt - MIN_OVERLAY_LEN)) };
        }
        if (drag.mode === 'resize-r') {
          return { ...o, end: Math.min(duration || 0, Math.max(t, drag.startAt + MIN_OVERLAY_LEN)) };
        }
        return o;
      }));
    };

    const onUp = () => {
      stopAutoScroll();

      if (drag.mode === 'create') {
        const s  = ghostCreate ? Math.max(0, Math.min(ghostCreate.start, ghostCreate.end)) : drag.anchorTime;
        const en = ghostCreate ? Math.min(duration || 0, Math.max(ghostCreate.start, ghostCreate.end)) : drag.anchorTime;
        if (en - s >= MIN_OVERLAY_LEN) {
          const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
          setOverlays(prev => [...prev, { id, ...defaultOverlay(s, en) }]);
          setSelectedOverlayId(id);
          setPanelTab("inspector");
        } else {
          // Tiny drag = plain click on empty track → seek there
          handleSeek(drag.anchorTime);
        }
        // Swallow the click event that follows this mouseup either way,
        // so the scroller's click-to-seek doesn't double-fire.
        suppressClickRef.current = true;
        setGhostCreate(null);
      }

      // After a real move/resize drag, swallow the click on the overlay
      // block so it doesn't immediately re-seek to the clip start.
      if ((drag.mode === 'move' || drag.mode === 'resize-l' || drag.mode === 'resize-r') && movedRef.current) {
        suppressClickRef.current = true;
      }

      setDrag({ mode: null, overlayId: null, anchorTime: 0, startAt: 0, endAt: 0 });
    };

    const onTouchMove = (e) => {
      if (drag.mode && drag.mode !== 'create') e.preventDefault();
      if (e.touches.length > 0) onMove(e);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [drag, getMouseTime, duration, ghostCreate, handleSeek, runAutoScroll, stopAutoScroll]);

  // ─── Clips + FX visible at playhead (WYSIWYG before process) ─────
  const visibleOverlays = useMemo(
    () => overlaysAtPlayhead(currentTime, overlays),
    [currentTime, overlays]
  );

  const hasEditsAtPlayhead = useMemo(
    () => visibleOverlays.length > 0 || hasAnyGlobalFx(fx),
    [visibleOverlays, fx]
  );

  const vignetteBg = useMemo(() => computeVignetteCss(fx.vignette / 100), [fx.vignette]);
  const globalFilterCss = useMemo(() => computeGlobalFilterCss(fx), [fx]);
  const fadeOpacity = useMemo(
    () => computeFadeOpacity(currentTime, duration, fx.fadeIn, fx.fadeOut),
    [currentTime, duration, fx.fadeIn, fx.fadeOut]
  );

  const apiBase = api?.defaults?.baseURL || window.location.origin;

  // ─── Video Events ──────────────────────────────────────────────
  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    const onLoaded = () => setDuration(v.duration || 0);
    const onTime = () => setCurrentTime(v.currentTime || 0);
    const onEnded = () => setIsPlaying(false);
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("ended", onEnded);
    };
  }, [videoUrl]);

  // ─── RAF: playhead + noise ─────────────────────────────────────
  useEffect(() => {
    const noiseStrength = fx.noise / 100;
    function tick() {
      const v = videoRef.current;
      if (v) setCurrentTime(v.currentTime || 0);
      const canvas = noiseCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        const w = Math.max(2, Math.floor(canvas.clientWidth || 320));
        const h = Math.max(2, Math.floor(canvas.clientHeight || 180));
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const prev = lastTimeRef.current || now;
        const dt = Math.max(0, now - prev);
        lastTimeRef.current = now;
        ctx.clearRect(0, 0, w, h);
        const parts = particlesRef.current;
        for (let i = parts.length - 1; i >= 0; i--) if (now - parts[i].birth >= parts[i].life) parts.splice(i, 1);
        if (noiseStrength > 0) {
          const rate = noiseStrength * 0.003 * w * h;
          const expected = rate * (dt / 1000) + (spawnCarryRef.current || 0);
          const toSpawn = Math.floor(expected);
          spawnCarryRef.current = expected - toSpawn;
          for (let i = 0; i < toSpawn; i++) {
            parts.push({ x: Math.random() * w, y: Math.random() * h, r: Math.max(0.5, dotSize), birth: now, life: Math.max(40, blinkMs) });
          }
        }
        ctx.fillStyle = '#000';
        for (const p of parts) {
          let a = Math.sin(Math.PI * ((now - p.birth) / p.life));
          a = Math.pow(a, 1.25);
          ctx.globalAlpha = a * 0.9;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.closePath(); ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [fx.noise, dotSize, blinkMs]);

  // Sync overlay videos to base playhead when inside their clip
  useEffect(() => {
    const base = videoRef.current;
    if (!base) return;
    visibleOverlays.forEach((ov) => {
      if (ov.type !== "video" || !ov.url?.trim()) return;
      const el = document.getElementById(`tl-ov-video-${ov.id}`);
      if (!el) return;
      const expected = Math.max(0, base.currentTime - ov.start);
      if (Math.abs((el.currentTime || 0) - expected) > 0.06) {
        try { el.currentTime = expected; } catch (_) {}
      }
      if (isPlaying && el.paused) el.play().catch(() => {});
      if (!isPlaying && !el.paused) { try { el.pause(); } catch (_) {} }
    });
  }, [currentTime, visibleOverlays, isPlaying]);

  // ─── Files ────────────────────────────────────────────────────
  const handleBaseFilePick = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (prevBaseObjectUrlRef.current) {
      try { URL.revokeObjectURL(prevBaseObjectUrlRef.current); objectUrlsRef.current.delete(prevBaseObjectUrlRef.current); } catch (_) {}
    }
    prevBaseObjectUrlRef.current = url;
    objectUrlsRef.current.add(url);
    setVideoUrl(url);
    setVideoFileName(file.name || "");
    const v = videoRef.current; if (v) v.load();
  };

  useEffect(() => () => {
    try { objectUrlsRef.current.forEach(u => URL.revokeObjectURL(u)); objectUrlsRef.current.clear(); } catch (_) {}
  }, []);

  // Cleanup auto-scroll RAF on unmount
  useEffect(() => () => {
    if (autoScrollRaf.current) cancelAnimationFrame(autoScrollRaf.current);
  }, []);

  // ─── Follow Playhead ──────────────────────────────────────────
  useEffect(() => {
    if (!followPlayhead) return;
    const el = timelineScrollRef.current; if (!el) return;
    const px = currentTime * pixelsPerSecond;
    if (px < el.scrollLeft || px > el.scrollLeft + el.clientWidth - 60) {
      el.scrollLeft = Math.max(0, px - el.clientWidth * 0.4);
    }
  }, [currentTime, pixelsPerSecond, followPlayhead]);

  // ─── Wheel Handler ────────────────────────────────────────────
  useEffect(() => {
    const el = timelineScrollRef.current; if (!el) return;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const beforeX = el.scrollLeft + e.clientX - el.getBoundingClientRect().left;
        const timeAtCursor = beforeX / pixelsPerSecond;
        const z = Math.max(40, Math.min(1600, pixelsPerSecond * (e.deltaY < 0 ? 1.1 : 0.9)));
        setPixelsPerSecond(z);
        requestAnimationFrame(() => {
          el.scrollLeft = Math.max(0, timeAtCursor * z - (e.clientX - el.getBoundingClientRect().left));
        });
        return;
      }
      e.preventDefault();
      el.scrollLeft += Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [pixelsPerSecond]);

  // ─── Overlay Helpers ──────────────────────────────────────────
  const selectedOverlay = useMemo(
    () => overlays.find(o => o.id === selectedOverlayId) || null,
    [overlays, selectedOverlayId]
  );

  const updateSelected = (patch) => {
    if (!selectedOverlayId) return;
    setOverlays(prev => prev.map(o => o.id === selectedOverlayId ? { ...o, ...patch } : o));
  };

  const removeOverlay = useCallback((id) => {
    setOverlays(prev => prev.filter(o => o.id !== id));
    setSelectedOverlayId(prev => (prev === id ? null : prev));
  }, []);

  const applyUrlToOverlay = (id, url) => {
    setOverlays(prev => prev.map(o => o.id === id ? { ...o, url, type: detectType(url) } : o));
  };

  const togglePlay = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    // Drive off the element's own state, not the (possibly stale) React state
    if (!v.paused) { v.pause(); setIsPlaying(false); }
    else { v.play().then(() => setIsPlaying(true)).catch(() => {}); }
  }, []);

  // ─── Keyboard shortcuts ───────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedOverlayId) {
        e.preventDefault(); removeOverlay(selectedOverlayId);
      }
      if (e.key === " ") { e.preventDefault(); togglePlay(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); handleSeek(Math.max(0, currentTime - 5)); }
      if (e.key === "ArrowRight") { e.preventDefault(); handleSeek(Math.min(duration, currentTime + 5)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedOverlayId, currentTime, duration, handleSeek, removeOverlay, togglePlay]);

  // Quick URL commit
  const commitQuickUrl = useCallback(() => {
    const url = quickUrl.trim();
    if (!url) return;
    if (selectedOverlayId) {
      applyUrlToOverlay(selectedOverlayId, url);
    } else {
      const start = Math.min(currentTime, Math.max(0, (duration || 0) - DEFAULT_NEW_LEN));
      const end = Math.min(duration || (start + DEFAULT_NEW_LEN), start + DEFAULT_NEW_LEN);
      const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      setOverlays(prev => [...prev, { id, ...defaultOverlay(start, end), url, type: detectType(url) }]);
      setSelectedOverlayId(id);
    }
    setQuickUrl("");
  }, [quickUrl, selectedOverlayId, currentTime, duration]);

  // ─── Timeline Data ────────────────────────────────────────────
  const timelineWidth = Math.max(1, (duration || 0) * pixelsPerSecond);

  const majorTicks = useMemo(() => {
    const arr = []; const d = Math.ceil(duration || 0);
    for (let s = 0; s <= d; s++) arr.push({ s, x: s * pixelsPerSecond });
    return arr;
  }, [duration, pixelsPerSecond]);

  const minorTicks = useMemo(() => {
    if (pixelsPerSecond < 400) return [];
    const arr = [];
    const dms = Math.ceil((duration || 0) * 10);
    for (let i = 0; i <= dms; i++) {
      const t = i / 10, x = t * pixelsPerSecond;
      if (Math.abs(t - Math.round(t)) < 1e-6) continue;
      arr.push({ t, x });
    }
    return arr;
  }, [duration, pixelsPerSecond]);

  const overlayRects = useMemo(() => overlays.map(o => ({
    id: o.id, x: o.start * pixelsPerSecond,
    w: (o.end - o.start) * pixelsPerSecond,
    type: o.type, url: o.url, uploading: o.uploading
  })), [overlays, pixelsPerSecond]);

  // ─── Thumbnails ────────────────────────────────────────────────
  useEffect(() => {
    if (!videoUrl || !duration) { setThumbs([]); return; }
    let aborted = false;

    async function generateEverySecond() {
      try {
        const cap = document.createElement('video');
        cap.crossOrigin = "anonymous"; cap.preload = "auto"; cap.src = videoUrl;
        captureVideoRef.current = cap;
        await new Promise(res => { if (cap.readyState >= 1) return res(); cap.addEventListener('loadedmetadata', res, { once: true }); });
        const times = [];
        for (let t = 0; t < duration; t += Math.max(1, thumbEverySec)) times.push(t);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const targetH = 40;
        const results = [];
        for (const t of times) {
          if (aborted) break;
          await new Promise(res => {
            const onSeeked = () => { cap.removeEventListener('seeked', onSeeked); res(); };
            cap.currentTime = Math.min(duration - 0.001, Math.max(0, t));
            cap.addEventListener('seeked', onSeeked);
          });
          const vw = cap.videoWidth || 640, vh = cap.videoHeight || 360;
          const scale = targetH / vh;
          const tw = Math.max(1, Math.round(vw * scale));
          canvas.width = tw; canvas.height = targetH;
          ctx.drawImage(cap, 0, 0, tw, targetH);
          results.push({ t, dataUrl: canvas.toDataURL('image/jpeg'), w: tw, h: targetH });
        }
        if (!aborted) setThumbs(results);
      } catch { setThumbs([]); }
    }

    generateEverySecond();
    return () => { aborted = true; };
  }, [videoUrl, duration, thumbEverySec, exactFpsMode]);

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div className="tl-shell">

      {/* ── Top Bar ────────────────────────────── */}
      <div className="tl-topbar">
        <div className="tl-topbar-title">Timeline Editor</div>
        {videoFileName && (
          <>
            <div className="tl-topbar-divider" />
            <div className="tl-topbar-filename">{videoFileName}</div>
          </>
        )}
        <div className="tl-topbar-spacer" />
        <label className="tl-btn tl-btn-green">
          Pick File
          <input type="file" accept="video/*" style={{ display: 'none' }}
            onChange={(e) => handleBaseFilePick(e.target.files?.[0])} />
        </label>
        <button className="tl-process-btn" onClick={sendVideoMetadataToBackend}>
          ▶ Process Edits
        </button>
      </div>

      {/* ── Main Layout ────────────────────────── */}
      <div className="tl-main">

        {/* ── Center Column ──────────────────── */}
        <div className="tl-center">

          {/* Video Section */}
          <div className="tl-video-section">
            <div className="tl-video-loader">
              <input
                className="tl-input tl-input-url"
                placeholder="Paste video URL (mp4 / webm / mov…)"
                value={videoUrl}
                onChange={(e) => {
                  const url = e.target.value;
                  setVideoUrl(url);
                  try {
                    const fn = new URL(url).pathname.split('/').pop();
                    if (fn) setVideoFileName(fn);
                  } catch (_) {}
                }}
              />
              <button className="tl-btn" onClick={() => {
                const v = videoRef.current; if (v) { v.load(); setTimeout(() => setDuration(v.duration || 0), 200); }
              }}>Load</button>
            </div>

            {/* Preview — compact centered player (CapCut-style) */}
            <div className="tl-player-stage">
            <div className="tl-preview-wrap">
              {/* Composite layer: base video + overlays. The global color
                  filter applies to this whole group — same order as the
                  export graph (overlays first, then color FX). */}
              <div className="tl-composite" style={{ filter: globalFilterCss }}>
                <video ref={videoRef} src={videoUrl || undefined}
                  className="tl-preview-video" crossOrigin="anonymous" controls={false} />
                <div className="tl-overlay-layer">
                  {visibleOverlays.map((ov) => {
                    const ken = computeKenScale(ov, currentTime);
                    const groupStyle = computeOverlayGroupStyle(ov, currentTime);
                    const mediaStyle = computeOverlayMediaStyle(ov, ken, currentTime);
                    const bgConf = computeOverlayBgStyle(ov);
                    const src = resolveOverlaySrc(ov.url, apiBase);
                    if (!src) {
                      return (
                        <div key={ov.id} className="tl-overlay-placeholder">
                          <span>Overlay clip — add image/video in Inspector</span>
                        </div>
                      );
                    }
                    const media = ov.type === "video" ? (
                      <video
                        id={`tl-ov-video-${ov.id}`}
                        src={src}
                        muted
                        playsInline
                        className="tl-overlay-media"
                        style={{ ...mediaStyle, transition: 'transform 0.033s linear' }}
                      />
                    ) : (
                      <img
                        src={src}
                        alt="overlay"
                        className="tl-overlay-media"
                        style={{ ...mediaStyle, transition: 'transform 0.033s linear' }}
                      />
                    );
                    return (
                      <div key={ov.id} className="tl-ov-group"
                        style={{ ...groupStyle, transition: 'transform 0.033s linear, opacity 0.05s linear' }}>
                        {bgConf && (bgConf.kind === 'image'
                          ? <img src={src} alt="" className="tl-ov-bg" style={bgConf.style} />
                          : <div className="tl-ov-bg" style={bgConf.style} />)}
                        {media}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="tl-vignette" style={{ background: vignetteBg, opacity: fx.vignette > 0 ? 1 : 0 }} />
              <canvas ref={noiseCanvasRef} className="tl-noise-canvas"
                style={{ opacity: fx.noise > 0 ? 1 : 0 }} />
              {/* Whole-video fade in/out — matches ffmpeg fade filters */}
              <div className="tl-fade-layer" style={{ opacity: fadeOpacity }} />
              {!hasEditsAtPlayhead && overlays.some(o => isTimeInOverlay(currentTime, o)) && (
                <div className="tl-preview-hint">Playhead on overlay clip — add media in Inspector to preview</div>
              )}
            </div>
            </div>
          </div>

          {/* ── Transport Bar ──────────────────── */}
          <div className="tl-transport">
            <button className="tl-btn tl-btn-accent" onClick={togglePlay}
              style={{ minWidth: 56 }}>
              {isPlaying ? "⏸" : "▶"}
              <span className="tl-transport-hide-xs"> {isPlaying ? "Pause" : "Play"}</span>
            </button>
            <button className="tl-btn" onClick={() => handleSeek(Math.max(0, currentTime - 5))}>−5s</button>
            <button className="tl-btn" onClick={() => handleSeek(Math.min(duration, currentTime + 5))}>+5s</button>
            <button className="tl-btn tl-transport-hide-xs" onClick={() => handleSeek(0)}>⏮</button>
            <button className="tl-btn tl-transport-hide-xs" onClick={() => handleSeek(duration)}>⏭</button>
            <div className="tl-transport-time">
              {formatClockFull(currentTime)} / {formatClockFull(duration)}
            </div>
            <div style={{ flex: 1 }} />
            <label className="tl-transport-label tl-vol-range">Vol</label>
            <input type="range" className="tl-range tl-vol-range" min={0} max={100} defaultValue={100}
              style={{ width: 60 }}
              onChange={(e) => { if (videoRef.current) videoRef.current.volume = Number(e.target.value) / 100; }} />
          </div>

          {/* ── Quick URL Bar ───────────────────── */}
          <div className="tl-quick-url-bar">
            <span className="tl-quick-label">Quick Overlay</span>
            <input
              className="tl-input" style={{ flex: 1, minWidth: 140 }}
              placeholder="Paste image/video link → Enter to apply or create at playhead"
              value={quickUrl}
              onChange={(e) => setQuickUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitQuickUrl(); } }}
              onBlur={commitQuickUrl}
            />
            <label className="tl-toggle-wrap">
              <input type="checkbox" checked={followPlayhead} onChange={(e) => setFollowPlayhead(e.target.checked)} />
              <span className="tl-toggle-label">Follow</span>
            </label>
          </div>

          {/* ── Timeline ────────────────────────── */}
          <div className="tl-timeline-section">
            <div className="tl-timeline-toolbar">
              <button
                className="tl-btn tl-btn-accent tl-add-overlay-btn"
                onClick={createOverlayAtPlayhead}
                title="Add overlay at playhead position"
              >
                + Overlay
              </button>

              <div className="tl-toolbar-divider" />

              <span className="tl-toolbar-label">Zoom</span>
              <input type="range" className="tl-range" min={40} max={1600} value={pixelsPerSecond}
                onChange={(e) => setPixelsPerSecond(Number(e.target.value))}
                style={{ width: 100 }} />
              <span className="tl-zoom-display">{Math.round(pixelsPerSecond)}px/s</span>
              <div style={{ flex: 1 }} />
              <span className="tl-toolbar-label">Thumbs every</span>
              <input type="number" min={1} max={60} value={thumbEverySec}
                onChange={(e) => setThumbEverySec(Math.max(1, Number(e.target.value) || 1))}
                className="tl-input" style={{ width: 52, padding: '4px 8px' }} />
              <span className="tl-toolbar-label">sec</span>
              <label className="tl-toggle-wrap">
                <input type="checkbox" checked={exactFpsMode} onChange={(e) => setExactFpsMode(e.target.checked)} />
                <span className="tl-toggle-label" style={{ fontSize: 11 }}>Exact FPS</span>
              </label>
            </div>

            {/* Scrollable timeline */}
            <div
              className="tl-scroll"
              ref={timelineScrollRef}
              onClick={(e) => {
                // Swallow the click that follows a drag (create/move/resize)
                if (suppressClickRef.current) { suppressClickRef.current = false; return; }
                const target = e.target;
                if (target.dataset?.role) return; // overlays / handles / playhead
                const scroller = e.currentTarget;
                const rect = scroller.getBoundingClientRect();
                const x = e.clientX - rect.left + scroller.scrollLeft - TIMELINE_PAD_LEFT;
                handleSeek(x / pixelsPerSecond);
              }}
            >
              <div style={{ width: timelineWidth + TIMELINE_PAD_LEFT * 2, paddingLeft: TIMELINE_PAD_LEFT, minHeight: 108 }}>
                {/* Ruler */}
                <div className="tl-ruler">
                  <div className="tl-ruler-inner" style={{ width: timelineWidth }}>
                    {majorTicks.map((m) => (
                      <div key={`M${m.s}`} style={{ position: "absolute", left: m.x }}>
                        <div className="tl-major-tick" />
                        <div className="tl-tick-label">{formatClock(m.s)}</div>
                      </div>
                    ))}
                    {minorTicks.map((mi, idx) => (
                      <div key={`m${idx}`} style={{ position: "absolute", left: mi.x }}>
                        <div className="tl-minor-tick" />
                      </div>
                    ))}
                    {thumbs.map((th, i) => (
                      <img key={`th${i}`} src={th.dataUrl} alt=""
                        className="tl-thumb-img"
                        style={{ left: th.t * pixelsPerSecond - th.w / 2 }}
                        draggable={false} />
                    ))}
                  </div>
                </div>

                {/* Track — drag on empty space to create an overlay */}
                <div className="tl-track-area">
                  <div
                    className="tl-track"
                    style={{ width: timelineWidth }}
                    onMouseDown={beginCreate}
                    onTouchStart={beginCreate}
                  >
                    {ghostCreate && (
                      <div className="tl-ghost-create" style={{
                        left: Math.min(ghostCreate.start, ghostCreate.end) * pixelsPerSecond,
                        width: Math.max(2, Math.abs(ghostCreate.end - ghostCreate.start) * pixelsPerSecond),
                      }} />
                    )}

                    {overlayRects.map((r) => {
                      const selected = selectedOverlayId === r.id;
                      const label = r.url
                        ? r.url.split('/').pop().split('?')[0].substring(0, 22)
                        : "empty — click to set URL";
                      return (
                        <div
                          key={r.id}
                          data-role="overlay"
                          className={`tl-overlay-block${selected ? ' selected' : ''}${r.uploading ? ' uploading' : ''}`}
                          style={{ left: r.x, width: Math.max(2, r.w) }}
                          title={r.url || "(no url)"}
                          onMouseDown={(e) => beginMove(r.id, e)}
                          onTouchStart={(e) => beginMove(r.id, e)}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (suppressClickRef.current) { suppressClickRef.current = false; return; }
                            setSelectedOverlayId(r.id);
                            setPanelTab("inspector");
                            const ov = overlays.find((o) => o.id === r.id);
                            if (ov) handleSeek(Math.min(duration || ov.start, Math.max(0, ov.start + 0.02)));
                          }}
                        >
                          <div className="tl-overlay-label">{label}</div>
                          <div
                            data-role="handle"
                            className="tl-overlay-handle tl-overlay-handle--left"
                            onMouseDown={(e) => beginResize('l', r.id, e)}
                            onTouchStart={(e) => beginResize('l', r.id, e)}
                          />
                          <div
                            data-role="handle"
                            className="tl-overlay-handle tl-overlay-handle--right"
                            onMouseDown={(e) => beginResize('r', r.id, e)}
                            onTouchStart={(e) => beginResize('r', r.id, e)}
                          />
                        </div>
                      );
                    })}

                    {/* Playhead */}
                    <div className="tl-playhead-line" style={{ left: currentTime * pixelsPerSecond }}>
                      <div className="tl-playhead-head" />
                    </div>
                    <div className="tl-playhead-grab" data-role="playhead"
                      style={{ left: currentTime * pixelsPerSecond }}
                      onMouseDown={onPlayheadMouseDown}
                      onTouchStart={onPlayheadMouseDown} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Panel ─────────────────────── */}
        <div className="tl-panel">

          {/* Panel Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--tl-border)' }}>
            {['inspector', 'fx', 'list'].map(tab => (
              <button key={tab} className="tl-btn" onClick={() => setPanelTab(tab)} style={{
                flex: 1, borderRadius: 0, border: 'none', borderBottom: panelTab === tab
                  ? '2px solid var(--tl-accent)' : '2px solid transparent',
                color: panelTab === tab ? 'var(--tl-accent-bright)' : 'var(--tl-text-muted)',
                background: 'transparent', fontSize: 10, fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase', padding: '10px 4px',
              }}>
                {tab === 'inspector' ? '✏️' : tab === 'fx' ? '🎨' : '☰'}
                <span style={{ marginLeft: 4, display: 'inline' }}>{tab}</span>
              </button>
            ))}
          </div>

          {/* ── Inspector Tab ────────────────── */}
          {panelTab === 'inspector' && (
            <div className="tl-panel-section">
              <div className="tl-panel-title">Overlay Inspector</div>
              {!selectedOverlay ? (
                <div className="tl-no-selection">
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🎬</div>
                  <div>Select an overlay block on the timeline or drag to create a new one.</div>
                  <div style={{ marginTop: 8, fontSize: 11 }}>Del / Backspace to remove selected</div>
                </div>
              ) : (
                <>
                  <div className="tl-inspector-field">
                    <span className="tl-inspector-label">URL / Link</span>
                    <input
                      id="overlay-url-input"
                      className="tl-input" style={{ width: '100%' }}
                      placeholder="Paste image/video URL…"
                      value={selectedOverlay.url || ""}
                      onChange={(e) => updateSelected({ url: e.target.value, type: detectType(e.target.value) })}
                    />
                  </div>

                  <div className="tl-inspector-field">
                    <span className="tl-inspector-label">Pick File</span>
                    <label className="tl-btn tl-btn-green" style={{ width: '100%', justifyContent: 'center' }}>
                      Upload file
                      <input type="file" accept="image/*,video/*" style={{ display: 'none' }}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f || !selectedOverlay) return;
                          const tempUrl = URL.createObjectURL(f);
                          updateSelected({ url: tempUrl, type: f.type?.startsWith("video/") ? "video" : "image", uploading: true });
                          try {
                            const serverUrl = await uploadFileToBackend(f);
                            updateSelected({ url: serverUrl, uploading: false });
                          } catch (err) {
                            alert('Upload failed: ' + err.message);
                            updateSelected({ url: '', uploading: false });
                          }
                        }} />
                    </label>
                  </div>

                  <div className="tl-inspector-field">
                    <span className="tl-inspector-label">Type</span>
                    <select className="tl-input" style={{ width: '100%' }}
                      value={selectedOverlay.type || "image"}
                      onChange={(e) => updateSelected({ type: e.target.value })}>
                      <option value="image">Image</option>
                      <option value="video">Video (muted)</option>
                    </select>
                  </div>

                  <div className="tl-inspector-field">
                    <span className="tl-inspector-label">Opacity</span>
                    <input type="range" className="tl-range" style={{ width: '100%' }}
                      min={0} max={100}
                      value={Math.round((selectedOverlay.opacity ?? 1) * 100)}
                      onChange={(e) => updateSelected({ opacity: clamp(Number(e.target.value) / 100, 0, 1) })} />
                  </div>

                  {/* Framing: fit mode + focus point (for Fill/crop) */}
                  <div className="tl-inspector-field">
                    <span className="tl-inspector-label">Framing</span>
                    <select className="tl-input" style={{ width: '100%', marginBottom: 6 }}
                      value={selectedOverlay.fit === 'cover' ? 'cover' : 'contain'}
                      onChange={(e) => updateSelected({ fit: e.target.value })}>
                      {OVERLAY_FIT_MODES.map((m) => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                    </select>
                    {selectedOverlay.fit === 'cover' && (
                      <>
                        <div className="tl-fx-row">
                          <span className="tl-fx-label">Focus X</span>
                          <input type="range" className="tl-range tl-fx-range" min={0} max={100}
                            value={Math.round((selectedOverlay.focus?.x ?? 0.5) * 100)}
                            onChange={(e) => updateSelected({ focus: { ...(selectedOverlay.focus || {}), x: Number(e.target.value) / 100, y: selectedOverlay.focus?.y ?? 0.5 } })} />
                          <span className="tl-fx-value">{Math.round((selectedOverlay.focus?.x ?? 0.5) * 100)}%</span>
                        </div>
                        <div className="tl-fx-row">
                          <span className="tl-fx-label">Focus Y</span>
                          <input type="range" className="tl-range tl-fx-range" min={0} max={100}
                            value={Math.round((selectedOverlay.focus?.y ?? 0.5) * 100)}
                            onChange={(e) => updateSelected({ focus: { x: selectedOverlay.focus?.x ?? 0.5, y: Number(e.target.value) / 100 } })} />
                          <span className="tl-fx-value">{Math.round((selectedOverlay.focus?.y ?? 0.5) * 100)}%</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Background fill — letterboxed images only */}
                  {selectedOverlay.type !== 'video' && selectedOverlay.fit !== 'cover' && (
                    <div className="tl-inspector-field">
                      <span className="tl-inspector-label">Background</span>
                      {(() => {
                        const bg = selectedOverlay.bg || defaultOverlayBg();
                        const setBg = (patch) => updateSelected({ bg: { ...bg, ...patch } });
                        return (
                          <>
                            <select className="tl-input" style={{ width: '100%', marginBottom: 6 }}
                              value={bg.type || 'none'}
                              onChange={(e) => setBg({ type: e.target.value })}>
                              {OVERLAY_BG_TYPES.map((b) => (
                                <option key={b.id} value={b.id}>{b.label}</option>
                              ))}
                            </select>
                            {bg.type === 'blur' && (
                              <div className="tl-fx-row">
                                <span className="tl-fx-label">Blur</span>
                                <input type="range" className="tl-range tl-fx-range" min={1} max={100}
                                  value={bg.blur ?? 20}
                                  onChange={(e) => setBg({ blur: Number(e.target.value) })} />
                                <span className="tl-fx-value">{bg.blur ?? 20}px</span>
                              </div>
                            )}
                            {(bg.type === 'color' || bg.type === 'gradient') && (
                              <div className="tl-fx-row">
                                <span className="tl-fx-label">{bg.type === 'gradient' ? 'Colors' : 'Color'}</span>
                                <input type="color" className="tl-color-input"
                                  value={bg.color || '#000000'}
                                  onChange={(e) => setBg({ color: e.target.value })} />
                                {bg.type === 'gradient' && (
                                  <input type="color" className="tl-color-input"
                                    value={bg.color2 || '#7c3aed'}
                                    onChange={(e) => setBg({ color2: e.target.value })} />
                                )}
                              </div>
                            )}
                            {bg.type === 'gradient' && (
                              <div className="tl-fx-row">
                                <span className="tl-fx-label">Angle</span>
                                <input type="range" className="tl-range tl-fx-range" min={0} max={360}
                                  value={bg.angle ?? 180}
                                  onChange={(e) => setBg({ angle: Number(e.target.value) })} />
                                <span className="tl-fx-value">{bg.angle ?? 180}°</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  <div className="tl-inspector-field">
                    <span className="tl-inspector-label">Timing</span>
                    <div className="tl-inspector-row">
                      <input type="number" step="0.01" min="0" className="tl-input tl-inspector-input"
                        placeholder="Start (s)" value={selectedOverlay.start ?? ""}
                        onChange={(e) => updateSelected({ start: Math.max(0, Math.min(Number(e.target.value) || 0, duration || 0)) })} />
                      <span style={{ color: 'var(--tl-text-muted)', fontSize: 11 }}>→</span>
                      <input type="number" step="0.01" min="0" className="tl-input tl-inspector-input"
                        placeholder="End (s)" value={selectedOverlay.end ?? ""}
                        onChange={(e) => updateSelected({ end: Math.max(0, Math.min(Number(e.target.value) || 0, duration || 0)) })} />
                    </div>
                    <div style={{ marginTop: 4, fontSize: 11, color: 'var(--tl-text-muted)', fontFamily: 'DM Mono, monospace' }}>
                      Duration: {selectedOverlay ? Math.max(0, selectedOverlay.end - selectedOverlay.start).toFixed(2) : 0}s
                    </div>
                  </div>

                  {/* Transitions — in and out, each with duration */}
                  {[
                    ['transIn', 'Transition In'],
                    ['transOut', 'Transition Out'],
                  ].map(([key, label]) => {
                    const t = selectedOverlay[key] || defaultTransition();
                    const isVideoOv = selectedOverlay.type === 'video';
                    return (
                      <div className="tl-inspector-field" key={key}>
                        <span className="tl-inspector-label">{label}</span>
                        <div className="tl-inspector-row">
                          <select className="tl-input" style={{ flex: 1, minWidth: 120 }}
                            value={t.type}
                            onChange={(e) => updateSelected({ [key]: { ...t, type: e.target.value } })}>
                            {TRANSITIONS.map((tr) => (
                              <option key={tr.id} value={tr.id} disabled={tr.imagesOnly && isVideoOv}>
                                {tr.label}
                              </option>
                            ))}
                          </select>
                          {t.type !== 'none' && (
                            <input type="number" step="0.1" min="0.1" max="5"
                              className="tl-input" style={{ width: 64 }}
                              title="Duration (s)"
                              value={t.duration}
                              onChange={(e) => updateSelected({ [key]: { ...t, duration: clamp(Number(e.target.value) || 0.5, 0.05, 5) } })} />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Ken Burns */}
                  <div className="tl-inspector-field">
                    <span className="tl-inspector-label">Ken Burns</span>
                    <label className="tl-toggle-wrap" style={{ marginBottom: 8 }}>
                      <input type="checkbox" checked={!!selectedOverlay.ken?.enabled}
                        onChange={(e) => updateSelected({ ken: { ...(selectedOverlay.ken || {}), enabled: e.target.checked } })} />
                      <span className="tl-toggle-label">Enabled</span>
                    </label>
                    {selectedOverlay.ken?.enabled && (
                      <>
                        <select className="tl-input" style={{ width: '100%', marginBottom: 8 }}
                          value={selectedOverlay.ken?.mode || 'auto'}
                          onChange={(e) => updateSelected({ ken: { ...(selectedOverlay.ken || {}), mode: e.target.value } })}>
                          <option value="in">Zoom In</option>
                          <option value="out">Zoom Out</option>
                          <option value="auto">Auto (in/out)</option>
                        </select>
                        <div className="tl-ken-row">
                          <span className="tl-fx-label">Strength</span>
                          <input type="range" className="tl-range tl-fx-range" min={0} max={50}
                            value={Math.round((selectedOverlay.ken?.strength ?? 0.2) * 100)}
                            onChange={(e) => updateSelected({ ken: { ...(selectedOverlay.ken || {}), strength: Number(e.target.value) / 100 } })} />
                          <span className="tl-fx-value">{Math.round((selectedOverlay.ken?.strength ?? 0.2) * 100)}%</span>
                        </div>
                        {selectedOverlay.ken?.mode === 'auto' && (
                          <div className="tl-ken-row">
                            <span className="tl-fx-label">Speed</span>
                            <input type="range" className="tl-range tl-fx-range" min={2} max={20}
                              value={selectedOverlay.ken?.speed ?? 8}
                              onChange={(e) => updateSelected({ ken: { ...(selectedOverlay.ken || {}), speed: Number(e.target.value) } })} />
                            <span className="tl-fx-value">{selectedOverlay.ken?.speed ?? 8}s</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <button className="tl-btn tl-btn-danger" style={{ width: '100%' }}
                    onClick={() => removeOverlay(selectedOverlayId)}>
                    🗑 Delete Overlay
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── FX Tab — generated from GLOBAL_FX_CONTROLS ─────── */}
          {panelTab === 'fx' && (
            <div className="tl-panel-section">
              <div className="tl-panel-title">Visual FX</div>

              {GLOBAL_FX_CONTROLS.map((ctrl) => (
                <div className="tl-fx-row" key={ctrl.key}>
                  <span className="tl-fx-label">{ctrl.label}</span>
                  <input type="range" className="tl-range tl-fx-range"
                    min={ctrl.min} max={ctrl.max} step={ctrl.step}
                    value={fx[ctrl.key]}
                    onChange={(e) => setFxVal(ctrl.key, Number(e.target.value))} />
                  <span className="tl-fx-value">{fx[ctrl.key]}{ctrl.unit}</span>
                </div>
              ))}

              <div className="tl-panel-title" style={{ marginTop: 14 }}>Noise Look</div>
              <div className="tl-fx-row">
                <span className="tl-fx-label">Dot Size</span>
                <input type="range" className="tl-range tl-fx-range" min={1} max={10} value={dotSize}
                  onChange={(e) => setDotSize(Number(e.target.value))} />
                <span className="tl-fx-value">{dotSize}px</span>
              </div>
              <div className="tl-fx-row">
                <span className="tl-fx-label">Blink</span>
                <input type="range" className="tl-range tl-fx-range" min={40} max={400} value={blinkMs}
                  onChange={(e) => setBlinkMs(Number(e.target.value))} />
                <span className="tl-fx-value">{blinkMs}ms</span>
              </div>

              <button className="tl-btn" style={{ width: '100%', marginTop: 10 }}
                onClick={() => setFx(defaultGlobalFx())}>
                ↺ Reset all effects
              </button>
            </div>
          )}

          {/* ── List Tab ────────────────────────── */}
          {panelTab === 'list' && (
            <div className="tl-panel-section">
              <div className="tl-panel-title">All Overlays ({overlays.length})</div>
              {overlays.length === 0 ? (
                <div className="tl-no-selection">
                  <div style={{ fontSize: 20, marginBottom: 8 }}>📂</div>
                  Drag on the timeline to create overlays, or paste a link in the Quick Overlay bar.
                </div>
              ) : overlays.map(o => (
                <div key={o.id}
                  className={`tl-overlay-list-item${selectedOverlayId === o.id ? ' selected' : ''}`}
                  onClick={() => { setSelectedOverlayId(o.id); setPanelTab('inspector'); }}>
                  <span className="tl-pill">{o.type}</span>
                  <span className="tl-overlay-list-url" title={o.url || "(no url)"}>
                    {o.url ? o.url.split('/').pop().split('?')[0] || o.url : "(empty)"}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--tl-text-muted)', fontFamily: 'DM Mono, monospace', flexShrink: 0 }}>
                    {o.start.toFixed(1)}s–{o.end.toFixed(1)}s
                  </span>
                  <button className="tl-btn" style={{ padding: '3px 8px', fontSize: 11 }}
                    onClick={(e) => { e.stopPropagation(); handleSeek(o.start); }}>Go</button>
                  <button className="tl-btn tl-btn-danger" style={{ padding: '3px 8px', fontSize: 11 }}
                    onClick={(e) => { e.stopPropagation(); removeOverlay(o.id); }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Keyboard shortcuts hint */}
          <div className="tl-panel-section" style={{ borderBottom: 'none' }}>
            <div className="tl-panel-title">Shortcuts</div>
            {[
              ['Space', 'Play / Pause'],
              ['← →', 'Seek ±5s'],
              ['Del / ⌫', 'Delete selected'],
              ['Drag track', 'Create overlay'],
              ['Ctrl+Scroll', 'Zoom timeline'],
              ['Scroll', 'Pan timeline'],
            ].map(([key, desc]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--tl-accent-bright)', background: 'rgba(124,58,237,0.1)', padding: '2px 8px', borderRadius: 4 }}>{key}</span>
                <span style={{ fontSize: 11, color: 'var(--tl-text-muted)' }}>{desc}</span>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}